import { useState } from 'react';
import { Link } from 'wouter';
import {
  ArrowRight,
  Mail,
  Lock,
  Sparkles,
  Check,
  Building2,
  Clock,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

// API URL from environment variable, fallback to relative path (Vite proxies to localhost:3000)
const API_URL = import.meta.env.VITE_API_URL || '';

export function SignIn() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source: 'signin',
          metadata: {
            referrer: document.referrer || undefined,
            utm_source: new URLSearchParams(window.location.search).get('utm_source') || undefined,
            utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
            utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Unable to connect. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="w-full max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left Column - Sign In for existing users */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-4">
                  <Lock className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  Welcome Back
                </h1>
                <p className="text-slate-600">
                  Already have access? Sign in to your dashboard.
                </p>
              </div>

              <a
                href="https://app.dakkio.io"
                className="w-full flex items-center justify-center px-6 py-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all hover:shadow-lg mb-6"
              >
                Sign In to Dashboard
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>

              <div className="text-center">
                <p className="text-sm text-slate-500">
                  Having trouble signing in?{' '}
                  <a href="mailto:support@dakkio.io" className="text-primary-600 hover:text-primary-700 font-medium">
                    Contact support
                  </a>
                </p>
              </div>
            </div>

            {/* Right Column - Request Access */}
            <div className="bg-gradient-to-br from-primary-600 to-secondary-500 rounded-2xl shadow-xl p-8 text-white">
              <div className="text-center mb-6">
                <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-4">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-semibold">Early Access</span>
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  Request an Invite
                </h2>
                <p className="text-white/90 text-sm leading-relaxed">
                  We're currently onboarding a select group of companies to ensure the best experience. Join the waitlist to be notified when spots open up.
                </p>
              </div>

              {/* Status indicators */}
              <div className="bg-white/10 rounded-xl p-4 mb-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Building2 className="w-5 h-5 text-white/80" />
                  <span className="text-white/90 text-sm">
                    <span className="font-semibold text-white">Limited early access</span> is now live
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-white/80" />
                  <span className="text-white/90 text-sm">
                    Select partners already using the platform
                  </span>
                </div>
              </div>

              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your work email"
                      className="w-full pl-12 pr-4 py-4 bg-white text-slate-900 rounded-xl placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-white/50"
                      required
                    />
                  </div>
                  {error && (
                    <div className="bg-red-100 text-red-700 px-4 py-3 rounded-xl text-sm">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-4 bg-white text-primary-600 rounded-xl font-semibold hover:bg-slate-50 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      <>
                        Request Early Access
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <div className="bg-white/20 rounded-xl p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-full mb-3">
                    <Check className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold mb-2">You're on the list!</h3>
                  <p className="text-white/90 text-sm">
                    We'll notify you at <span className="font-medium">{email}</span> when your invite is ready.
                  </p>
                </div>
              )}

              <p className="text-xs text-white/70 text-center mt-4">
                Priority given to teams building IoT, sensor, or time-series applications.
              </p>
            </div>
          </div>

          {/* Bottom info */}
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">
              Want to learn more before signing up?
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link href="/product">
                <a className="text-primary-600 hover:text-primary-700 font-semibold flex items-center">
                  Explore Features
                  <ArrowRight className="ml-1 w-4 h-4" />
                </a>
              </Link>
              <span className="text-slate-300">|</span>
              <a
                href="https://docs.dakkio.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 font-semibold flex items-center"
              >
                Read Documentation
                <ArrowRight className="ml-1 w-4 h-4" />
              </a>
              <span className="text-slate-300">|</span>
              <Link href="/pricing">
                <a className="text-primary-600 hover:text-primary-700 font-semibold flex items-center">
                  View Pricing
                  <ArrowRight className="ml-1 w-4 h-4" />
                </a>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
